import { useEffect, useState } from 'react'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { type Site, parseSiteSettings, WP_ROLES } from './shared'
import { useUpdateSite } from '../../hooks/useSites'

interface SettingsTabProps {
  site: Site
}

// All available providers
const ALL_PROVIDERS = ['google', 'microsoft', 'github'] as const
type Provider = (typeof ALL_PROVIDERS)[number]

export function SettingsTab({ site }: SettingsTabProps) {
  const settings = parseSiteSettings(site.settings)

  // Use local state for immediate UI updates
  const [enabledProviders, setEnabledProviders] = useState<Provider[]>(() =>
    (settings.enabled_providers as Provider[] | undefined) || [...ALL_PROVIDERS]
  )
  const [oauthOnly, setOauthOnly] = useState<boolean>(() => settings.disable_password_login ?? false)
  // Track whether role restrictions feature is enabled (key exists in settings)
  const [roleRestrictionsEnabled, setRoleRestrictionsEnabled] = useState<boolean>(() =>
    'password_allowed_roles' in settings
  )
  const [passwordAllowedRoles, setPasswordAllowedRoles] = useState<string[]>(() =>
    settings.password_allowed_roles ?? []
  )
  const [oauthOptOut, setOauthOptOut] = useState<boolean>(() => site.oauth_opt_out === 1)
  const [oauthOptOutNote, setOauthOptOutNote] = useState<string>(() => site.oauth_opt_out_note || '')

  // Sync local state when site prop changes (e.g., switching sites)
  useEffect(() => {
    const newSettings = parseSiteSettings(site.settings)
    setEnabledProviders((newSettings.enabled_providers as Provider[] | undefined) || [...ALL_PROVIDERS])
    setOauthOnly(newSettings.disable_password_login ?? false)
    setRoleRestrictionsEnabled('password_allowed_roles' in newSettings)
    setPasswordAllowedRoles(newSettings.password_allowed_roles ?? [])
    setOauthOptOut(site.oauth_opt_out === 1)
    setOauthOptOutNote(site.oauth_opt_out_note || '')
  }, [site.domain, site.settings, site.oauth_opt_out, site.oauth_opt_out_note])

  const updateSite = useUpdateSite()

  const handleOAuthOnlyToggle = async (enabled: boolean) => {
    const previousValue = oauthOnly
    setOauthOnly(enabled) // Optimistic update

    try {
      await updateSite.mutateAsync({
        domain: site.domain,
        data: { settings: { ...settings, disable_password_login: enabled } }
      })
      toast.success(enabled ? 'OAuth Only enabled - password form will be hidden' : 'Password login enabled')
    } catch (err) {
      setOauthOnly(previousValue) // Rollback on error
      toast.error(`Failed to update setting: ${String(err)}`)
    }
  }

  const handleProviderToggle = async (provider: Provider, enabled: boolean) => {
    const current = [...enabledProviders]
    const updated = enabled
      ? [...current, provider]
      : current.filter(p => p !== provider)

    // Prevent disabling all providers
    if (updated.length === 0) {
      toast.error('At least one provider must be enabled')
      return
    }

    setEnabledProviders(updated as Provider[]) // Optimistic update

    try {
      await updateSite.mutateAsync({
        domain: site.domain,
        data: { settings: { ...settings, enabled_providers: updated } }
      })
      toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} ${enabled ? 'enabled' : 'disabled'}`)
    } catch (err) {
      setEnabledProviders(current) // Rollback on error
      toast.error(`Failed to update provider: ${String(err)}`)
    }
  }

  const handleRoleRestrictionsToggle = async (enabled: boolean) => {
    const previousEnabled = roleRestrictionsEnabled
    const previousRoles = [...passwordAllowedRoles]

    setRoleRestrictionsEnabled(enabled) // Optimistic update

    if (enabled) {
      // Enable: persist empty array to activate the feature
      setPasswordAllowedRoles([])
      try {
        await updateSite.mutateAsync({
          domain: site.domain,
          data: { settings: { ...settings, password_allowed_roles: [] } }
        })
        toast.success('Role restrictions enabled - configure which roles can use password login')
      } catch (err) {
        setRoleRestrictionsEnabled(previousEnabled)
        setPasswordAllowedRoles(previousRoles)
        toast.error(`Failed to enable role restrictions: ${String(err)}`)
      }
    } else {
      // Disable: remove the key from settings
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { password_allowed_roles: _, ...settingsWithoutRoles } = settings
      try {
        await updateSite.mutateAsync({
          domain: site.domain,
          data: { settings: settingsWithoutRoles }
        })
        toast.success('Role restrictions disabled - all users can use password login')
      } catch (err) {
        setRoleRestrictionsEnabled(previousEnabled)
        toast.error(`Failed to disable role restrictions: ${String(err)}`)
      }
    }
  }

  const handleRoleToggle = async (role: string, checked: boolean) => {
    const current = [...passwordAllowedRoles]
    const updated = checked
      ? [...current, role]
      : current.filter(r => r !== role)

    setPasswordAllowedRoles(updated) // Optimistic update

    try {
      await updateSite.mutateAsync({
        domain: site.domain,
        data: { settings: { ...settings, password_allowed_roles: updated } }
      })
      if (updated.length === 0) {
        toast.success('All roles now require OAuth for login')
      } else {
        toast.success(`Password login ${checked ? 'enabled' : 'disabled'} for ${role}s`)
      }
    } catch (err) {
      setPasswordAllowedRoles(current) // Rollback on error
      toast.error(`Failed to update role setting: ${String(err)}`)
    }
  }

  return (
    <div className="space-y-6">
      {/* OAuth Only Toggle */}
      <div className="flex items-center justify-between p-4 rounded-lg border">
        <div>
          <Label htmlFor="oauth-only-settings" className="font-medium">OAuth Only</Label>
          <div className="text-sm text-muted-foreground">
            Hide password login form, require SSO sign-in
          </div>
        </div>
        <Switch
          id="oauth-only-settings"
          checked={oauthOnly}
          onCheckedChange={handleOAuthOnlyToggle}
          disabled={updateSite.isPending}
        />
      </div>

      {/* Login Providers */}
      <div className="space-y-3">
        <div className="font-medium">Login Providers</div>
        <div className="text-sm text-muted-foreground mb-2">
          Choose which OAuth providers are available on this site's login page
        </div>
        <div className="space-y-2">
          {ALL_PROVIDERS.map((provider) => (
            <div key={provider} className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5">
                  {provider === 'google' && (
                    <svg viewBox="0 0 24 24" className="w-5 h-5">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  {provider === 'microsoft' && (
                    <svg viewBox="0 0 24 24" className="w-5 h-5">
                      <path fill="#F25022" d="M1 1h10v10H1z"/>
                      <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                      <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                      <path fill="#FFB900" d="M13 13h10v10H13z"/>
                    </svg>
                  )}
                  {provider === 'github' && (
                    <svg viewBox="0 0 24 24" className="w-5 h-5">
                      <path fill="currentColor" d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
                    </svg>
                  )}
                </div>
                <Label htmlFor={`provider-${provider}`} className="capitalize font-normal">
                  {provider}
                </Label>
              </div>
              <Switch
                id={`provider-${provider}`}
                checked={enabledProviders.includes(provider)}
                onCheckedChange={(checked) => handleProviderToggle(provider, checked)}
                disabled={updateSite.isPending}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Role Restrictions - only show when OAuth Only is NOT enabled */}
      {!oauthOnly && (
        <div className="space-y-4">
          {/* Toggle to enable role restrictions */}
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div>
              <Label htmlFor="role-restrictions" className="font-medium">Restrict Password by Role</Label>
              <div className="text-sm text-muted-foreground">
                Only allow specific roles to use password login
              </div>
            </div>
            <Switch
              id="role-restrictions"
              checked={roleRestrictionsEnabled}
              onCheckedChange={handleRoleRestrictionsToggle}
              disabled={updateSite.isPending}
            />
          </div>

          {/* Role checkboxes - only show when restrictions are enabled */}
          {roleRestrictionsEnabled && (
            <div className="space-y-3 pl-4 border-l-2 border-muted">
              <div className="text-sm text-muted-foreground">
                Select roles that can use password login. Unchecked roles must use OAuth.
                {passwordAllowedRoles.length === 0 && (
                  <span className="block mt-1 text-yellow-600 dark:text-yellow-400">
                    No roles selected - all users must use OAuth (same as OAuth Only)
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {WP_ROLES.map((role) => (
                  <div key={role.value} className="flex items-center gap-2 p-2 rounded border">
                    <Checkbox
                      id={`role-${role.value}`}
                      checked={passwordAllowedRoles.includes(role.value)}
                      onCheckedChange={(checked) => handleRoleToggle(role.value, checked === true)}
                      disabled={updateSite.isPending}
                    />
                    <Label htmlFor={`role-${role.value}`} className="font-normal text-sm cursor-pointer">
                      {role.label}
                    </Label>
                  </div>
                ))}
              </div>
              {passwordAllowedRoles.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Password login allowed for: {passwordAllowedRoles.map(r =>
                    WP_ROLES.find(wr => wr.value === r)?.label || r
                  ).join(', ')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Client OAuth Opt-Out */}
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 rounded-lg border">
          <div>
            <Label htmlFor="oauth-opt-out" className="font-medium">Client OAuth Opt-Out</Label>
            <div className="text-sm text-muted-foreground">
              Record that the client has requested OAuth be disabled. For tracking only — does not change OAuth settings.
            </div>
          </div>
          <Switch
            id="oauth-opt-out"
            checked={oauthOptOut}
            onCheckedChange={async (enabled) => {
              const previousValue = oauthOptOut
              const previousNote = oauthOptOutNote
              setOauthOptOut(enabled)

              try {
                if (enabled) {
                  await updateSite.mutateAsync({
                    domain: site.domain,
                    data: { oauth_opt_out: 1, oauth_opt_out_note: oauthOptOutNote || null }
                  })
                  toast.success('Opt-out recorded — site flagged as client-requested disable')
                } else {
                  setOauthOptOutNote('')
                  await updateSite.mutateAsync({
                    domain: site.domain,
                    data: { oauth_opt_out: 0 }
                  })
                  toast.success('Opt-out removed')
                }
              } catch (err) {
                setOauthOptOut(previousValue)
                setOauthOptOutNote(previousNote)
                toast.error(`Failed to update opt-out: ${String(err)}`)
              }
            }}
            disabled={updateSite.isPending}
          />
        </div>

        {oauthOptOut && (
          <div className="space-y-3 pl-4 border-l-2 border-muted">
            <div className="text-sm text-muted-foreground">
              Why was OAuth disabled? (optional — helps the team remember later)
            </div>
            <Textarea
              placeholder="e.g. Client's web developer requested it — doesn't want users to need a GitHub/Google account"
              value={oauthOptOutNote}
              onChange={(e) => setOauthOptOutNote(e.target.value)}
              rows={2}
            />
            <div className="flex items-center justify-between">
              <Button
                size="sm"
                variant="outline"
                disabled={updateSite.isPending || oauthOptOutNote === (site.oauth_opt_out_note || '')}
                onClick={async () => {
                  try {
                    await updateSite.mutateAsync({
                      domain: site.domain,
                      data: { oauth_opt_out_note: oauthOptOutNote || null }
                    })
                    toast.success('Note saved')
                  } catch (err) {
                    toast.error(`Failed to save note: ${String(err)}`)
                  }
                }}
              >
                Save note
              </Button>
              {site.oauth_opt_out_at && (
                <span className="text-xs text-muted-foreground">
                  Opted out {new Date(site.oauth_opt_out_at + 'Z').toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Site Info */}
      <div className="space-y-2 text-sm">
        <div className="font-medium">Site Info</div>
        <div className="flex justify-between py-2 border-b">
          <span className="text-muted-foreground">Domain</span>
          <code className="font-mono">{site.domain}</code>
        </div>
        <div className="flex justify-between py-2 border-b">
          <span className="text-muted-foreground">Name</span>
          <span>{site.name || '-'}</span>
        </div>
        <div className="flex justify-between py-2 border-b">
          <span className="text-muted-foreground">ID</span>
          <code className="font-mono text-xs">{site.id}</code>
        </div>
      </div>
    </div>
  )
}
